import { Ionicons } from '@expo/vector-icons';
import { T, useT } from '@transifex/react';
import { Divider, Heading, HStack, Text, VStack } from '@vidahealth/vida-ui';
import { useState } from 'react';
import { Image, Linking, Platform, View } from 'react-native';

import avatar1 from '@/assets/images/onboarding/member-avatar-1.png';
import avatar2 from '@/assets/images/onboarding/member-avatar-2.png';
import avatar3 from '@/assets/images/onboarding/member-avatar-3.png';
import avatar4 from '@/assets/images/onboarding/member-avatar-4.png';
import qrCode from '@/assets/images/onboarding/qr-code.png';
import { trackEvent } from '@/modules/analytics';
import { sendMobileLink } from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, PhoneField, isValidPhoneDisplay } from '@/modules/ui';

const MEMBER_AVATARS = [avatar1, avatar2, avatar3, avatar4];
const NAVY = '#012D6A';
// Universal app-download link (Sparta's VIDA_APP_DOWNLOAD) — the page redirects
// to the right store. Used by the native "Download app" CTA.
const VIDA_APP_DOWNLOAD_URL = 'https://vida.com/smsdownload/';

function MemberProof() {
  return (
    <HStack space='lg' className='items-center'>
      <HStack className='items-center'>
        {MEMBER_AVATARS.map((src, i) => (
          <View
            key={i}
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              borderWidth: 2,
              borderColor: '#fff',
              marginLeft: i === 0 ? 0 : -10,
              overflow: 'hidden',
            }}
          >
            <Image source={src} style={{ width: '100%', height: '100%' }} resizeMode='cover' />
          </View>
        ))}
      </HStack>
      <VStack>
        <HStack style={{ alignItems: 'center', gap: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Ionicons key={i} name='star' size={14} color='#AB3192' />
          ))}
          <Ionicons name='star-half' size={14} color='#AB3192' />
        </HStack>
        <Text size='sm' className='font-body text-typography-600'>
          <T _str='Join 300k + Vida members' />
        </Text>
      </VStack>
    </HStack>
  );
}

/**
 * Private to TransitionToNativeAppScreen — the left content panel: download
 * heading, member social proof, and (web) a QR card + "text link" input, or
 * (native) a single Download CTA. The text-link button POSTs the number to the
 * SMS service (sendMobileLink) and surfaces a sent / error state.
 */
export function DownloadPanel() {
  const t = useT();
  const { user } = useRulesEngine();
  // `phone` holds the formatted `(xxx) xxx-xxxx` value (PhoneField masks input).
  const [phone, setPhone] = useState('');
  // Inline message under the input — used for BOTH the validation error and a
  // failed send (Sparta surfaces both as the field's helperText). null = none.
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const isNative = Platform.OS === 'ios' || Platform.OS === 'android';

  // Text the app-download link to the entered number (Sparta's sendMobileLink).
  // The button stays enabled: an empty / incomplete number surfaces the inline
  // validation message instead of POSTing (mirrors Sparta's stylos
  // DownloadVidaApp Yup gate), and a failed send drops the same inline error
  // under the input while the button returns to its idle state.
  const onTextLink = async () => {
    if (phone === '' || !isValidPhoneDisplay(phone)) {
      setPhoneError(t('Please enter a valid phone number'));
      return;
    }
    trackEvent('transition-click-send-link', {
      uuid: user?.uuid,
      // The text-link CTA only renders on web, so this is always desktop_web
      // today. TODO: mirror Sparta's mobile-web vs desktop-web detection (iframe
      // context / URL params, not viewport) when the native flow is wired up.
      platform: 'desktop_web',
    });
    setPhoneError(null);
    setStatus('sending');
    try {
      await sendMobileLink(phone);
      setStatus('sent');
      trackEvent('transition-send-link-success', { uuid: user?.uuid });
    } catch {
      setStatus('idle');
      setPhoneError(t('Something went wrong. Please try again.'));
    }
  };

  return (
    <VStack space='4xl' className='flex-1'>
      <VStack space='sm'>
        <Text size='lg' className='font-body-semibold text-primary-500'>
          <T _str='Download the app' />
        </Text>
        <Heading size='2xl' className='font-heading-semibold text-primary-900 dark:text-primary-0'>
          <T _str='For the best experience, download the mobile app' />
        </Heading>
      </VStack>

      <MemberProof />

      {isNative ? (
        <CtaButton
          onPress={() => {
            trackEvent('transition-click-download-app', { uuid: user?.uuid });
            void Linking.openURL(VIDA_APP_DOWNLOAD_URL);
          }}
          testID='transition-download-app'
        >
          <T _str='Download app' />
        </CtaButton>
      ) : (
        <>
          <HStack
            space='lg'
            style={{ backgroundColor: NAVY, borderRadius: 16, padding: 16, alignItems: 'center' }}
          >
            <View className='h-[140px] w-[140px] overflow-hidden rounded-vui-sm bg-white p-0.5'>
              <Image
                source={qrCode}
                style={{ width: '100%', height: '100%' }}
                resizeMode='contain'
                accessibilityLabel={t('Download Vida app QR code')}
              />
            </View>
            <VStack space='xs' className='flex-1'>
              <Text size='md' weight='bold' className='text-typography-0 dark:text-primary-0'>
                <T _str='Scan to download' />
              </Text>
              <Text size='md' className='text-typography-0 dark:text-primary-100'>
                <T _str='Point your phone’s camera at the QR code to get a link' />
              </Text>
            </VStack>
          </HStack>

          <HStack space='md' className='items-center'>
            <View className='flex-1'>
              <Divider />
            </View>
            <Text size='sm' weight='bold' className='text-typography-900'>
              <T _str='OR' />
            </Text>
            <View className='flex-1'>
              <Divider />
            </View>
          </HStack>

          <View className='flex-col gap-4 sm:flex-row sm:items-start'>
            <View className='min-w-[200px] sm:flex-1'>
              <PhoneField
                placeholder={t('Mobile phone number')}
                value={phone}
                onChangeText={(value) => {
                  setPhone(value);
                  setPhoneError(null);
                  setStatus('idle');
                }}
                // Lock the field while the request is in flight — the user can't
                // edit (which would otherwise re-enable the button and allow a
                // duplicate POST), and the row reads as a single busy unit.
                isDisabled={status === 'sending'}
                errorMessage={phoneError ?? undefined}
                testID='transition-phone'
              />
              {/* Success sits in the same slot the field error uses (mutually
                  exclusive — a send clears phoneError), matched to its margins. */}
              {status === 'sent' ? (
                <Text size='sm' className='ml-1 mt-2 text-success-700'>
                  <T _str='Link sent! Check your phone.' />
                </Text>
              ) : null}
            </View>
            <View className='mt-1 sm:flex-1'>
              <CtaButton
                isBusy={status === 'sending'}
                isDisabled={status === 'sending'}
                onPress={onTextLink}
                testID='transition-text-link'
              >
                <T _str='Text link to download' />
              </CtaButton>
            </View>
          </View>
        </>
      )}
    </VStack>
  );
}
